import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { brandShadowEffect } from '@/lib/styles';

const buttonVariants = cva(
  'inline-flex items-center text-dark-900 justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary border border-primary shadow hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive border border-destructive hover:bg-destructive/90',
        success: 'bg-success text-success border border-success hover:bg-success/90',
        outline: 'border border-input border-border hover:bg-dark-200 active:bg-dark-400 disabled:text-subtlest',
        surface: 'bg-surface text-dark-900 rounded border border-border hover:bg-dark-200 active:bg-dark-400',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        suggestion: 'bg-blue-600 text-white rounded-full',
        ghost: 'hover:bg-dark-200 active:bg-dark-400',
        link: 'text-foreground underline-offset-4 hover:underline',
        dark: 'bg-dark-700 text-white dark:text-primary shadow hover:bg-dark-800 dark:bg-primary dark:hover:bg-primary/80 focus-visible:bg-dark-800 dark:focus-visible:bg-primary/80',
      },
      size: {
        default: 'h-9 p-2',
        xs: 'h-8 rounded-md px-2 text-sm gap-1.5',
        sm: 'h-8 rounded-md px-3 text-sm',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9 [&_svg]:size-5',
      },
      elevation: {
        flat: '',
        shadow: `${brandShadowEffect}`,
      },
      disabled: {
        true: 'text-subtlest pointer-events-none bg-dark-400 border-dark-600 opacity-90',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      elevation: 'flat',
      disabled: false as boolean | null | undefined,
    },
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, elevation, disabled, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, elevation, disabled, className }))}
        ref={ref}
        {...props}
        disabled={disabled ?? undefined}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
